import { CommonModule, DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef } from 'ag-grid-community';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '../../features/users/users.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as selectPermissions from '../../core/permissions/permission.selector';
import {
  Component,
  HostListener,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,

  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';

@Component({
  selector: 'app-roles',
  imports: [
    AgGridAngular,
    CommonModule,
    MatTabsModule,
    FormsModule,
    MatSelectModule,
    MatInputModule,
    ReactiveFormsModule,

  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})

export class UsersComponent {
  readonly dialog = inject(MatDialog);
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';
  public selectedParent: number = 0;
  public parentChildUsers: any = null;
  typeOptions = ['Free', 'Monthly', 'Yearly'];
  membershipOptions = ['Free', 'Monthly', 'Yearly'];

  selectedType: string | null = null;
  selectedMembership: string | null = null;
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  private searchSubject = new Subject<string>();
  private appUserSearchSubject = new Subject<string>();
  selectedTabIndex: number = 0;
  userData: any[] = [];
  columnDefs: any[] = [];
  users: any[] = [];
  pageSize: number = 10;
  page: number = 0;
  totalPages: number = 1;
  totalItems: number = 0;
  pageSizeOptions = [5, 10, 25, 50];
  activeFilters: any = {};
  appUsersTypes: string[] = ['All', 'Free', 'AdFree', 'Premium', 'PremiumPlus'];
  appUserTypeForm: FormGroup;
  appUserType: string = 'All';
  searchTerm: string = '';
  appUserSearchTerm: string = '';
  canEditOrDelete: boolean = false;
  filterForm: FormGroup;

  constructor(
    private userService: UserService,
    private toastr: ToastrService,
    private store: Store,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      startDate: [''],
      endDate: [''],
      membership: [''],
      type: [''],
      marketing: [''],
    });
    this.appUserTypeForm = this.fb.group({
      appUserType: 'All',
    });

    this.filterForm.get('membership')?.valueChanges.subscribe((selectedMembership) => {
      const typeControl = this.filterForm.get('type');
      if (selectedMembership === 'isFree' || selectedMembership === 'isAdfree') {
        typeControl?.disable({ emitEvent: false });
        typeControl?.setValue('', { emitEvent: false });
      } else {
        typeControl?.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.checkViewportSize();
    this.loadAppUsers();
    this.checkPermissions();
    this.appUserSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((appUserSearchTerm) => {
        this.page = 0;
        this.loadAppUsers(appUserSearchTerm);
      });

    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.page = 0;
        this.appUserSearchTerm = searchTerm;
        this.loadAppUsers(searchTerm, this.activeFilters);
      });
  }

  exportUsers() {
    this.userService.exportAppUsers().subscribe({
      next: (data) => {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        const exportData = document.createElement('a');
        exportData.href = url;
        exportData.download = 'app_users.csv';
        exportData.click();

        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Export failed:', err);
      }
    });
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(UserFilterDialogComponent, {
      data: {
        filters: this.filterForm.value,
      },
      width: '720px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      if (result.action === 'apply') {
        this.filterForm.patchValue(result.filters || {});
        this.applyFilters();
      } else if (result.action === 'reset') {
        this.resetFilters();
      }
    });
  }

  onTypeChange(selectedType: string) {
    this.selectedType = selectedType;

    if (selectedType === 'Free') {
      this.membershipOptions = ['Free'];
      this.selectedMembership = 'Free';
    } else {
      this.membershipOptions = ['Free', 'Monthly', 'Yearly'];
    }
    this.applyFilters();
  }

  checkPermissions() {
    this.store
      .select(selectPermissions.hasPermission('UPDATE_USER'))
      .subscribe((canEdit) => {
        this.store
          .select(selectPermissions.hasPermission('DELETE_USER'))
          .subscribe((canDelete) => {
            this.canEditOrDelete = canEdit || canDelete;
            this.updateColumnDefs();
          });
      });
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.updateColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  private isDeleting = false;

  deleteAppUser(userId: number) {
    if (this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    const dialogRef = this.dialog.open(ConfirmDialog, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      this.isDeleting = false;

      if (!confirmed) {
        return;
      }

      this.userService.deleteAppUser(userId).subscribe({
        next: () => {
          this.toastr.success('User permanently deleted');
          this.rowData = this.rowData?.filter((u: any) => u.id !== userId) || [];
          this.totalItems = Math.max(0, this.totalItems - 1);
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);

          if (this.rowData) {
            this.rowData = [...this.rowData];
          }
        },
        error: (error) => {
          console.error('Delete error:', error);
        }
      });
    });
  }

  deleteAppIconRenderer(params: any) {
    const icon = document.createElement('img');
    icon.src = 'assets/images/icons/icon-delete.svg';
    icon.style.width = '20px';
    icon.style.cursor = 'pointer';

    const clonedIcon = icon.cloneNode(true) as HTMLImageElement;
    clonedIcon.onclick = (event) => {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        this.deleteAppUser(params.data.id);
      }, 0);
    };

    return clonedIcon;
  }

  editIconRenderer(params: any) {
    if (params.data?.role_key === 'super_admin') {
      return document.createTextNode('');
    }
    const icon = document.createElement('img');
    icon.src = 'assets/images/icons/icon-edit.svg';
    icon.alt = 'Edit';
    icon.style.width = '20px';
    icon.style.height = '20px';
    icon.style.cursor = 'pointer';
    icon.style.display = 'block';


    return icon;
  }

  deleteIconRenderer(params: any) {
    if (params.data?.role_key === 'super_admin') {
      return document.createTextNode('');
    }
    const icon = document.createElement('img');
    icon.src = 'assets/images/icons/icon-delete.svg';
    icon.alt = 'Delete';
    icon.style.width = '20px';
    icon.style.height = '20px';
    icon.style.cursor = 'pointer';
    icon.style.display = 'block';

    icon.addEventListener('click', () => {
    });
    return icon;
  }

  profileNameRenderer(params: any) {
    const link = document.createElement('span');
    link.textContent = params.value;
    link.style.color = '#3473D1';
    link.style.cursor = 'pointer';
    link.style.fontWeight = '400';
    return link;
  }

  handleViewClick(params: any) {
    this.openUserViewModal(params.data);
  }

  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'First Name',
        field: 'displayName',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: this.profileNameRenderer.bind(this),
        cellStyle: { cursor: 'pointer' },
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: this.profileNameRenderer.bind(this),
        cellStyle: { cursor: 'pointer' },
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Created On',
        field: 'createdAt',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Membership',
        field: 'memberShip',
        flex: this.viewPortSize ? 1 : 0,
        cellStyle: (params: any) => {
          switch (params.value) {
            case 'Premium Plus':
            case 'Premium':
            case 'Free':
            default:
              return '-';
          }
        },
      },
      {
        headerName: 'Type',
        field: 'billingCycle',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          if (params.data?.memberShip === 'Free') {
            return '';
          }
          return params.value || '-';
        },
      },
      {
        headerName: 'Marketing',
        field: 'marketing',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'OS',
        field: 'os',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Actions',
        width: 110,
        suppressSizeToFit: true,
        cellRenderer: (params: any) => {
          const wrapper = document.createElement('div');
          wrapper.classList.add('action-cell');
          wrapper.style.display = 'flex';
          wrapper.style.alignItems = 'center';
          wrapper.style.justifyContent = 'center';
          wrapper.style.gap = '12px';

          const viewIcon = document.createElement('img');
          viewIcon.src = 'assets/images/icons/view.svg';
          viewIcon.style.width = '18px';
          viewIcon.style.cursor = 'pointer';

          viewIcon.onclick = (event) => {
            event.stopPropagation();
            this.openUserViewModal(params.data.id, params.data);
          };

          const deleteIcon = this.deleteAppIconRenderer(params);

          wrapper.appendChild(viewIcon);
          wrapper.appendChild(deleteIcon);

          return wrapper;
        }
      }
    ];
  }

  onRowClicked(event: any): void {
    if (event?.event?.target?.closest('.action-cell')) {
      return;
    }

    const user = event.data;
    if (!user?.id) return;

    this.openUserViewModal(user.id, user);
  }

  openUserViewModal(userId: number, userData?: any) {
    const userFromList = this.rowData?.find(u => u.id === userId);
    if (userFromList && userFromList.childProfiles) {
      const dialogRef = this.dialog.open(UserViewDialogComponent, {
        width: '700px',
        maxWidth: '90vw',
        data: { user: userFromList },
        hasBackdrop: true,
        disableClose: false,
        autoFocus: false,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result.action === 'edit') {
            console.log('Edit user:', result.user);
          }
        }
      });
    } else {

      const dialogRef = this.dialog.open(UserViewDialogComponent, {
        width: '700px',
        maxWidth: '90vw',
        data: { userId: userId },
        hasBackdrop: true,
        disableClose: false,
        autoFocus: false,
      });

      setTimeout(() => {
        dialogRef.componentInstance.loadUserDetails();
      }, 100);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result.action === 'edit') {
            console.log('Edit user:', result.user);
          }
        }
      });
    }
  }

  onPortalUsersSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searching = searchTerm.length === 0 ? false : true;
    this.searchSubject.next(searchTerm);
  }

  searching: boolean = false;

  onAppUsersSearch(event: any): void {
    const search = event.target.value.trim();
    this.appUserSearchTerm = search;
    this.page = 0;
    this.loadAppUsers(search, this.activeFilters);
  }

  getMembershipType(user: any): string {
    if (user?.isPremiumPlus) return 'Premium Plus';
    if (user?.isPremium) return 'Premium';
    if (user?.isAdfree) return 'AdFree';
    if (user?.isFree) return 'Free';
    return '-';
  }

  applyFilters() {
    const filters = this.filterForm.value;

    const apiFilters = {
      startDate: filters.startDate ? this.formatDateForAPI(filters.startDate) : undefined,
      endDate: filters.endDate ? this.formatDateForAPI(filters.endDate) : undefined,
      membership: filters.membership || undefined,
      billingCycle: filters.type ? filters.type.toUpperCase() : undefined,
      isNewsletterSubscribed: this.getMarketingBoolean(filters.marketing),
    };

    this.activeFilters = apiFilters;
    this.page = 0;
    this.loadAppUsers(this.appUserSearchTerm, this.activeFilters);
  }

  resetFilters() {
    this.filterForm.reset();
    this.activeFilters = {};
    this.page = 0;
    this.loadAppUsers(this.appUserSearchTerm);
  }

  // Helper method to format date from YYYY-MM-DD to DD/MM/YY
  private formatDateForAPI(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);

    return `${day}/${month}/${year}`;
  }

  private getMarketingBoolean(marketingValue: string): boolean | undefined {
    if (marketingValue === 'Yes') return true;
    if (marketingValue === 'No') return false;
    return undefined;
  }

  loadAppUsers(search = '', filters?: any): void {
    const appliedFilters = filters || this.activeFilters || {};

    const limit = this.pageSize;
    const offset = this.page * this.pageSize;

    let finalFilters = { ...appliedFilters };

    if (this.appUserType !== 'All') {
      finalFilters.membership = this.getMembershipFilterFromAppUserType();
    }

    this.userService.getAppUsers(search, finalFilters, limit, offset).subscribe({
      next: (res: any) => {
        const users = res?.data || [];
        this.userData = users.map((user: any) => ({
          id: user.id,
          displayName: user?.displayName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          phoneNo: user?.phoneNo || '',
          createdAt: user?.createdAt
            ? new Date(user.createdAt).toLocaleDateString('en-GB')
            : '',
          memberShip: this.getMembershipType(user),
          marketing: user?.isNewsletterSubscribed ? 'Yes' : 'No',
          billingCycle: user?.subscriptions?.[0]?.plan?.billingCycle || '-',
          os: user?.os || '-',
          // Include all user data needed for the modal
          profilePic: user?.profilePic,
          status: user?.status,
          isNewsletterSubscribed: user?.isNewsletterSubscribed,
          isPremiumPlus: user?.isPremiumPlus,
          isPremium: user?.isPremium,
          isAdfree: user?.isAdfree,
          isFree: user?.isFree,
          // Include child profiles
          childProfiles: user?.childProfiles?.map((child: any) => ({
            id: child.id,
            displayName: child.displayName,
            lastName: child.lastName,
            age: child.age,
            gender: child.gender,
            secretPin: child.secretPin,
            avatar: child.avatar,
            theme: child.theme,
            isPremium: child.isPremium,
            status: child.status,
          })) || [],
        }));

        this.rowData = [...this.userData];
        this.totalPages = res.totalPages || 1;
        this.totalItems = res.totalItems || 0;
        this.page = res.currentPage ? res.currentPage - 1 : 0;
      },
      error: (err: any) => {
        console.error('Failed to load users:', err);
        this.toastr.error('Failed to load users');
        this.rowData = [];
      },
    });
  }
  private getMembershipFilterFromAppUserType(): string {
    switch (this.appUserType) {
      case 'PremiumPlus':
        return 'isPremiumPlus';

      case 'Premium':
        return 'isPremium';

      case 'AdFree':
        return 'isAdfree';

      case 'Free':
        return 'isFree';

      default:
        return '';
    }
  }

  fetchAllUsers(id: any): void {
    this.userService.getAllUsers(id).subscribe({
      next: (res) => {
        this.parentChildUsers = res;
      },
      error: (err) => {
        console.error('Failed to load child users:', err);
      },
    });
  }

  handleParent(parentIndex: number): void {
    this.selectedParent = parentIndex;
    const selectedUserId = this.userData![parentIndex]?.id;
    if (selectedUserId) {
      this.fetchAllUsers(selectedUserId);
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.loadAppUsers();
  }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.loadAppUsers(this.appUserSearchTerm, this.activeFilters);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.loadAppUsers(this.appUserSearchTerm, this.activeFilters);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.pageSize = newSize;
    this.page = 0;
    this.loadAppUsers(this.appUserSearchTerm, this.activeFilters);
  }
}
@Component({
  selector: 'app-user-filter-dialog',
  standalone: true,
  templateUrl: './filter-dialog/filter-dialog.component.html',
  styleUrls: ['./filter-dialog/filter-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogActions,
    MatDialogContent,
  ],
})
export class UserFilterDialogComponent implements OnInit {
  filterForm!: FormGroup;
  dateError: string | null = null;
  maxDateString: string = '';
  minEndDateString: string = '';

  constructor(private fb: FormBuilder, private dialogRef: MatDialogRef<UserFilterDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any) { }

  ngOnInit(): void {
    const today = new Date();
    this.maxDateString = today.toISOString().split('T')[0];
    const filters = this.data?.filters || {};

    this.filterForm = this.fb.group({
      startDate: [filters.startDate || ''],
      endDate: [filters.endDate || ''],
      membership: [filters.membership || ''],
      type: [filters.type || ''],
      marketing: [filters.marketing || ''],
    });
  }

  dateRangeValidator() {
    const startDate = this.filterForm.get('startDate')?.value;
    const endDate = this.filterForm.get('endDate')?.value;
    this.minEndDateString = startDate || '';

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      this.dateError = end < start ? 'End Date cannot be earlier than Start Date.' : null;
    } else {
      this.dateError = null;
    }
  }

  onApply(): void {
    if (this.dateError) return;
    this.dialogRef.close({
      action: 'apply',
      filters: this.filterForm.value,
    });
  }

  onReset(): void {
    this.filterForm.reset({
      startDate: '',
      endDate: '',
      membership: '',
      type: '',
      marketing: '',
    });
    this.dialogRef.close({ action: 'reset' });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'app-user-view-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './profile-details-modal/user-view-dialog.component.html',
  styleUrls: ['./profile-details-modal/user-view-dialog.component.scss']
})

export class UserViewDialogComponent implements OnInit {
  user: any;
  loading: boolean = true;
  error: string = '';

  constructor(
    private dialogRef: MatDialogRef<UserViewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { user?: any, userId?: number },
    private userService: UserService
  ) { }

  ngOnInit(): void {
    if (this.data.user) {
      this.user = this.data.user;
      this.loading = false;
    } else if (this.data.userId) {
      this.loadUserDetails();
    } else {
      this.error = 'No user data provided';
      this.loading = false;
    }
  }

  // Make this public so parent can call it if needed
  loadUserDetails(): void {
    this.loading = true;
    this.error = '';

    this.userService.getUserDetails(this.data.userId!).subscribe({
      next: (response) => {
        this.user = response;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading user details:', err);
        this.error = 'Failed to load user details. Please try again.';
        this.loading = false;
      }
    });
  }

  getSubscriptionType(): string {
    if (!this.user) return 'Unknown';

    if (this.user.isPremiumPlus) return 'Premium Plus';
    if (this.user.isPremium) return 'Premium';
    if (this.user.isAdfree) return 'AdFree';
    if (this.user.isFree) return 'Free';
    return 'Unknown';
  }

  getSubscriptionClass(): string {
    const type = this.getSubscriptionType().toLowerCase().replace(' ', '');
    return type;
  }

  close(): void {
    this.dialogRef.close();
  }

  editUser(): void {
    this.dialogRef.close({ action: 'edit', user: this.user });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private normalizeDates(): void {
    if (this.user?.createdAt) {
      this.user.createdAt = this.parseDate(this.user.createdAt);
    }
  }

  private parseDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (typeof dateValue === 'string' && dateValue.includes('-')) {
      return new Date(dateValue);
    }

    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      const [day, month, year] = dateValue.split('/');
      return new Date(+year, +month - 1, +day);
    }

    return null;
  }

  formatGenderClass(gender?: string): string {
  if (!gender) return '';

  return gender
    .toLowerCase()
    .replace(/\s+/g, '_');
}
}
